import { Row, buildActionColumn, buildAvatarDetailsColumn, buildExpandableCountColumn } from "@/components/Table";
import Tag from "@/components/Tag";
import { toUTCFormat } from "@/lib/dateHelpers";
import { Probation } from "./types";
import type { Action } from "@/components/MenuButton";
import { getProbationTypeLabel, type UniversityProbationType } from "./_constants";

export type StudentActiveProbationSummary = {
  count: number;
  displayStartDate: string;
  typedLines: string[];
};

export function buildStudentProbationsMap(
  nodes: Array<{
    startedAt: string;
    active: boolean;
    probationType?: UniversityProbationType | string | null;
    person: { id: string };
  }>
): Map<string, StudentActiveProbationSummary> {
  const grouped = new Map<
    string,
    Array<{ startedAt: string; probationType?: UniversityProbationType | string | null }>
  >();

  nodes.forEach((probation) => {
    if (!probation.active || !probation.startedAt) return;

    const existing = grouped.get(probation.person.id) ?? [];
    existing.push({ startedAt: probation.startedAt, probationType: probation.probationType });
    grouped.set(probation.person.id, existing);
  });

  const probationsMap = new Map<string, StudentActiveProbationSummary>();

  grouped.forEach((probations, personId) => {
    const sorted = [...probations].sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());

    const typedLines = sorted.map((probation) => {
      const typeLabel = getProbationTypeLabel(probation.probationType as UniversityProbationType | null | undefined);
      return `${typeLabel} (${toUTCFormat(probation.startedAt) ?? ""})`;
    });

    probationsMap.set(personId, {
      count: sorted.length,
      displayStartDate: sorted[0].startedAt,
      typedLines
    });
  });

  return probationsMap;
}

export function formatActiveProbationTooltip(summary: StudentActiveProbationSummary): string {
  if (summary.count === 1) {
    return `Student on Probation (${summary.typedLines[0]})`;
  }

  return `Student on Probation (${summary.count} active)\n${summary.typedLines.join("\n")}`;
}

export const mapProbations = (data: any[]): Probation[] => {
  if (!data) return [];
  return data.map((probation) => ({
    ...probation,
    person: {
      ...probation.person,
      displayName: probation.person.displayName || `${probation.person.firstName} ${probation.person.lastName}`
    }
  }));
};

type BuildColumnsProps = {
  isCompactLayout: boolean;
  navigate: (to: string) => void;
  location: { pathname: string; search: string };
  universityId: string;
  handleViewStudent: (probation: Probation) => void;
  setMenu: (menu: any) => void;
  actionId: (rowId: string) => string;
  handleOpenEndProbationDialog: (probation: Probation) => void;
  handleOpenEditProbationTypeDialog: (probation: Probation) => void;
  handleOpenAddNote?: (probation: Probation) => void;
};

export const buildColumns = ({
  isCompactLayout,
  navigate,
  location,
  universityId,
  handleViewStudent,
  setMenu,
  actionId,
  handleOpenEndProbationDialog,
  handleOpenEditProbationTypeDialog,
  handleOpenAddNote
}: BuildColumnsProps) => {
  const columns = [
    ...buildAvatarDetailsColumn<Probation>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName || `${row.person.firstName} ${row.person.lastName}`,
      getSubtitle: (row) => `PGA ID: ${row.person.pgaId}`,
      getPicture: (row) => row.person.profilePhoto,
      header: "Name",
      size: 300,
      minSize: 200
    }),
    {
      accessorKey: "startedAt",
      header: "Start Date",
      enableSorting: false,
      cell: ({ row }: { row: Row<Probation> }) => toUTCFormat(row.original.startedAt),
      size: 120,
      minSize: 100
    },
    {
      accessorKey: "endedAt",
      header: "End Date",
      enableSorting: false,
      cell: ({ row }: { row: Row<Probation> }) => (row.original.endedAt ? toUTCFormat(row.original.endedAt) : "--"),
      size: 120,
      minSize: 100
    },
    {
      accessorKey: "probationType",
      header: "Type",
      enableSorting: false,
      cell: ({ row }: { row: Row<Probation> }) => getProbationTypeLabel(row.original.probationType),
      size: 180,
      minSize: 140
    },
    buildExpandableCountColumn<Probation>({
      accessorKey: "notes",
      header: "Notes",
      getCount: (row) => row.notes?.length || 0,
      size: 100,
      minSize: 80
    }),
    {
      id: "ACTIVE",
      accessorKey: "active",
      header: "Status",
      accessorFn: (row: Probation) => (row.active ? "Active" : "Inactive"),
      cell: ({ row }: { row: Row<Probation> }) => (
        <Tag tagType={row.original.active ? "error" : "surface"} size="small">
          {row.original.active ? "Active" : "Inactive"}
        </Tag>
      ),
      meta: {
        sortType: "text" as const,
        sortLabel: "Status",
        headerClassName: "hide-header-in-column"
      },
      size: 80,
      minSize: 80
    },
    buildActionColumn<Probation>({
      getActions: (row) => {
        const actions: Action[] = [];

        if (row.active) {
          actions.push({
            section: "Manage",
            text: "End Probation",
            onClick: () => handleOpenEndProbationDialog(row)
          });

          actions.push({
            section: "Manage",
            text: "Edit Probation Type",
            onClick: () => handleOpenEditProbationTypeDialog(row)
          });
        }

        actions.push({
          section: "Actions",
          text: "View Student",
          onClick: () => {
            const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
            navigate(`/education/universities/${universityId}/students/${row.person.pgaId}?returnUrl=${returnUrl}`);
          }
        });

        if (handleOpenAddNote) {
          actions.push({
            section: "Actions",
            text: "Add Note",
            onClick: () => handleOpenAddNote(row)
          });
        }

        return actions;
      },
      onViewClick: (row) => handleViewStudent(row),
      viewButtonTitle: "View Student",
      actionId,
      setMenu
    })
  ];

  return columns;
};
